/* Nolae — Release Calendar (/pages/release-calendar).
 *
 * Two parts:
 *
 * 1. The chrome — what the prototype's public/glass.js did for its stand-in
 *    header, done for the theme's real one (the same approach as Idol A-Z's
 *    az-chrome.js): the header slides away on a sustained scroll down and back
 *    on a longer scroll up, armed only once the hero's opening has scrolled
 *    under it; --chrome-top tells everything pinned below where to park; and a
 *    frosted band (.rc-glass) grows behind whatever chrome is pinned — header,
 *    toolbar, day-view back bar — exactly as the prototype's body::before did.
 *
 * 2. The calendar — release-calendar/components/CalendarApp.tsx and the
 *    components it renders (CalendarGrid, CalendarHead, WeekAgenda, Feed,
 *    FeedRow, ArtistRow, FilterSheet, ViewToggle, CalModeFab), as plain
 *    functions drawing the same markup. State is the prototype's state; one
 *    render() redraws from it, the way React re-rendered from useState.
 *
 * Data: the prototype bakes releases[] from the Admin API at build time. Here
 * sections/release-calendar-data.liquid emits the "Pre-Order" collection 50
 * products a page (Liquid's cap) and the pages after the first are fetched
 * same-origin via ?section_id= — no Storefront API, no token. The artist name
 * is build-data.mjs's own extractArtist, run here instead of at build time.
 */

/* ———————————————— 1. Chrome ———————————————— */
(function () {
  "use strict";
  var root = document.documentElement;
  var header = document.querySelector('.shopify-section--header, [id^="shopify-section"][id$="__header"]');
  var page = document.querySelector(".nolae-rc");
  if (!page) return;

  var HERO_ENTRY = 0.2;
  var DELTA = 2, HIDE_AFTER = 24, REVEAL_AFTER = 120;
  var up = false, lastY = window.scrollY, run = 0;

  function headerSticky() {
    if (!header) return false;
    var p = getComputedStyle(header).position;
    return p === "sticky" || p === "fixed";
  }

  var lastTop = -1;
  function publishTop() {
    var top = 0;
    if (header && !up && headerSticky()) top = Math.max(0, header.getBoundingClientRect().top) + header.offsetHeight;
    top = Math.round(top);
    if (top !== lastTop) { root.style.setProperty("--chrome-top", top + "px"); lastTop = top; }
  }

  /* Armed once the header's bottom edge reaches the hero's first fifth — or,
     on a phone, the "Release Calendar" heading, since the artwork above it is
     most of the screen there. Same rule as the prototype's glass.js. */
  function pastHeroEntry() {
    var hero = page.querySelector(".hero");
    if (!hero || !header) return true;
    var mark = window.innerWidth <= 900 && page.querySelector(".hero-badge__label");
    var line;
    if (mark) line = mark.getBoundingClientRect().top;
    else { var r = hero.getBoundingClientRect(); line = r.top + r.height * HERO_ENTRY; }
    return line <= header.offsetHeight;
  }

  function setUp(v) {
    // Never while a theme drawer or this page's filter sheet is open.
    if (v && (document.body.classList.contains("lock-all") || root.classList.contains("rc-sheet-open"))) v = false;
    if (v === up) return;
    up = v;
    root.classList.toggle("nolae-chrome-up", v);
    publishTop();
    [80, 200, 340].forEach(function (ms) { setTimeout(function () { window.dispatchEvent(new Event("scroll")); }, ms); });
  }

  function autoHide() {
    if (!header) return;
    var y = window.scrollY;
    var diff = y - lastY;
    if (Math.abs(diff) < DELTA) return;
    lastY = y;
    if (diff > 0 !== run > 0) run = 0;
    run += diff;
    if (!pastHeroEntry()) { run = 0; setUp(false); return; }
    if (run >= HIDE_AFTER) setUp(true);
    else if (run <= -REVEAL_AFTER) setUp(false);
  }

  /* ————— The frosted band ————— */
  var glass = page.querySelector(".rc-glass");
  var settle = 0;
  function pinnedBottom(el) {
    if (!el || !el.isConnected) return 0;
    var cs = getComputedStyle(el);
    if (cs.position !== "sticky" && cs.position !== "fixed") return 0;
    var stick = parseFloat(cs.top) || 0;
    var r = el.getBoundingClientRect();
    return r.top <= stick + 1 && r.bottom > 0 ? r.bottom : 0;
  }
  function measureGlass() {
    if (!glass) return;
    var h = 0;
    if (window.scrollY > 0) {
      h = Math.max(pinnedBottom(header), pinnedBottom(page.querySelector(".toolbar")), pinnedBottom(page.querySelector(".day-focus-bar")));
    }
    root.style.setProperty("--rc-glass-h", h.toFixed(1) + "px");
    // Only animates while already on screen and changing size — never drops in.
    if (settle) { clearTimeout(settle); settle = 0; }
    if (h > 0) settle = setTimeout(function () { root.style.setProperty("--rc-glass-ease", "height 0.2s ease-in-out"); settle = 0; }, 100);
    else root.style.setProperty("--rc-glass-ease", "none");
  }

  /* ————— Header link colours —————
     The theme's header-color-adapt section, once the header is `scrolled`,
     samples what is behind each nav link / icon and forces it white over a
     dark patch. On this page that misfires: the pink hero reads as dark, but
     the header has its own light frosted surface, so the links go white on
     near-white — and the script bails out as soon as the header un-scrolls
     without undoing it, leaving them white on the solid white header back at
     the top. Here the header only ever sits over the hero or this page's own
     light glass, so the theme's own colours are always right: whatever the
     adapter writes is taken straight back off. MutationObserver callbacks run
     before paint, so the white never shows. */
  var adapted = document.querySelectorAll(".header__linklist-link, .header__secondary-links .header__icon-wrapper svg");
  function unadapt(el) { if (el.style.getPropertyValue("color")) el.style.removeProperty("color"); }
  if (adapted.length && window.MutationObserver) {
    var mo = new MutationObserver(function (records) { records.forEach(function (r) { unadapt(r.target); }); });
    [].forEach.call(adapted, function (el) { unadapt(el); mo.observe(el, { attributes: true, attributeFilter: ["style"] }); });
  }

  function onScroll() { autoHide(); publishTop(); measureGlass(); }
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", function () { publishTop(); measureGlass(); });
  window.addEventListener("rc:layout", measureGlass);
  if (window.ResizeObserver) {
    var ro = new ResizeObserver(function () { publishTop(); measureGlass(); });
    if (header) ro.observe(header);
    var tb = page.querySelector(".toolbar");
    if (tb) ro.observe(tb);
  }
  publishTop();
  measureGlass();
})();

/* ———————————————— 2. Calendar ———————————————— */
(function () {
  "use strict";
  var page = document.querySelector(".nolae-rc");
  if (!page) return;
  var docEl = document.documentElement;

  var S = {};
  try { S = JSON.parse(document.getElementById("rc-strings").textContent); } catch (e) {}
  var LOCALE = S.locale || docEl.lang || "de";
  function fill(tpl, vars) {
    return String(tpl || "").replace(/\{(\w+)\}/g, function (m, k) { return vars[k] != null ? vars[k] : m; });
  }
  function releasesLabel(n) { return fill(n === 1 ? S.releasesCountOne : S.releasesCountOther, { n: n }); }

  /* ————— lib/format.ts ————— */
  function todayISO() { return new Date().toISOString().slice(0, 10); }
  function addDays(iso, days) {
    var d = new Date(iso + "T00:00:00Z");
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
  }
  function mondayOf(iso) {
    var d = new Date(iso + "T00:00:00Z");
    return addDays(iso, -((d.getUTCDay() + 6) % 7));
  }
  function daysUntil(iso) {
    return Math.round((new Date(iso + "T00:00:00").getTime() - new Date(todayISO() + "T00:00:00").getTime()) / 86400000);
  }
  function fmtDate(iso, opts) {
    return new Intl.DateTimeFormat(LOCALE, opts || { day: "numeric", month: "short" }).format(new Date(iso + "T00:00:00"));
  }

  /* ————— scripts/build-data.mjs ————— */
  function titleCase(s) {
    return s.toLowerCase().replace(/(^|[\s\-(])\S/g, function (c) { return c.toUpperCase(); });
  }
  function extractArtist(richText, title) {
    if (richText) {
      try {
        var doc = JSON.parse(richText);
        var walk = function (n) { return n.value || (n.children || []).map(walk).join(""); };
        var text = walk(doc).trim();
        if (text) {
          var cut = text.split("(")[0].trim();
          if (cut && cut.length < 40) return cut;
        }
      } catch (e) {}
    }
    var t = title.split(" - ")[0].trim();
    return t.length < 40 ? titleCase(t) : "Various";
  }
  function prepare(raw) {
    var ship = raw.ship ? String(raw.ship).trim() : null;
    if (!raw.release && !ship) return null;
    return {
      id: raw.h,
      handle: raw.h,
      title: raw.t,
      url: raw.url || "/products/" + raw.h,
      artist: extractArtist(raw.artist, raw.t),
      image: raw.img || null,
      releaseDate: raw.release || null,
      shipLabel: ship,
      variants: (raw.v || []).map(function (v) {
        return { p: +v.p, pc: v.pc, c: v.c != null ? +v.c : null, cc: v.cc };
      }),
    };
  }
  /** lib/format.ts priceDisplay, on Liquid's own money strings. */
  function priceDisplay(r) {
    if (!r.variants.length) return { now: "", was: null };
    var byP = r.variants.slice().sort(function (a, b) { return a.p - b.p; });
    var lo = byP[0], hi = byP[byP.length - 1];
    var now = lo.p === hi.p ? lo.pc : lo.pc + " – " + hi.pc;
    if (!r.variants.some(function (v) { return v.c != null && v.c > v.p; })) return { now: now, was: null };
    var orig = r.variants.map(function (v) { return { n: v.c != null ? v.c : v.p, s: v.cc || v.pc }; });
    var oLo = orig.reduce(function (a, b) { return b.n < a.n ? b : a; });
    var oHi = orig.reduce(function (a, b) { return b.n > a.n ? b : a; });
    return { now: now, was: oLo.n === oHi.n ? oLo.s : oLo.s + " – " + oHi.s };
  }

  /* ————— Loading every page ————— */
  function readPage(doc) {
    var d = doc.getElementById("rc-data"), m = doc.getElementById("rc-meta");
    return { items: d ? JSON.parse(d.textContent) : [], pages: m ? JSON.parse(m.textContent).pages || 1 : 1 };
  }
  function fetchPage(n) {
    return fetch(window.location.pathname + "?section_id=release-calendar-data&page=" + n, { credentials: "same-origin" })
      .then(function (r) { return r.text(); })
      .then(function (html) { return readPage(new DOMParser().parseFromString(html, "text/html")).items; });
  }
  function toReleases(rawList) {
    var seen = {}, out = [];
    rawList.forEach(function (raw) {
      if (!raw || seen[raw.h]) return;
      seen[raw.h] = true;
      var r = prepare(raw);
      if (r) out.push(r);
    });
    return out;
  }

  /* ————— CalendarApp constants ————— */
  var CAROUSEL_MONTHS = 12;
  var HERO_CROSSED = 0.8;
  var PREV_MONTH_FROM = 20;
  var PAST_FLOOR = (function () {
    var p = todayISO().slice(0, 7).split("-").map(Number);
    return new Date(Date.UTC(p[0], p[1] - 2, PREV_MONTH_FROM)).toISOString().slice(0, 10);
  })();
  var months = (function () {
    var p = PAST_FLOOR.slice(0, 7).split("-").map(Number), out = [];
    for (var i = 0; i < CAROUSEL_MONTHS; i++) out.push(new Date(Date.UTC(p[0], p[1] - 1 + i, 1)).toISOString().slice(0, 7));
    return out;
  })();
  var bounds = { min: months[0], max: months[months.length - 1] };

  /* ————— State (CalendarApp's useState) ————— */
  var releases = [];
  var view = "cal";
  var month = todayISO().slice(0, 7);
  if (months.indexOf(month) === -1) month = months[0];
  var calMode = "month";
  var weekStart = mondayOf(todayISO());
  var weekSel = null;
  var pastHero = false;
  var dayFocus = null;
  var artists = {};
  var artistsN = 0;
  var pickerOpen = false;
  var filtersOpen = false;
  var pickerQ = "", sheetQ = "";

  /* ————— Derived (CalendarApp's useMemo) ————— */
  function pool() { return releases.filter(function (r) { return !r.releaseDate || r.releaseDate >= PAST_FLOOR; }); }
  function filtered() {
    var p = pool();
    return artistsN ? p.filter(function (r) { return artists[r.artist]; }) : p;
  }
  function artistCounts() {
    var m = {};
    pool().forEach(function (r) { m[r.artist] = (m[r.artist] || 0) + 1; });
    return Object.keys(m).map(function (a) { return [a, m[a]]; }).sort(function (a, b) { return b[1] - a[1]; });
  }

  /* ————— DOM ————— */
  function $(sel) { return page.querySelector(sel); }
  var railWrap = $("[data-rc-rail]"), railList = $("[data-rc-rail-list]");
  var toolbar = $("[data-rc-toolbar]"), viewToggle = $("[data-rc-view-toggle]");
  var filtersBtn = $("[data-rc-filters-open]"), artistToggle = $("[data-rc-artist-toggle]");
  var artistPicker = $("[data-rc-artist-picker]"), artistPop = $("[data-rc-artist-pop]");
  var artistSearch = $("[data-rc-artist-search]"), artistList = $("[data-rc-artist-list]");
  var summary = $("[data-rc-summary]"), summaryText = $("[data-rc-summary-text]");
  var main = $("[data-rc-main]"), calHead = $("[data-rc-cal-head]"), body = $("[data-rc-body]");

  /* The month heading and the weekday row float under the toolbar once
     scrolled past, as one unit (.cal-sticky). The weekday labels normally live
     inside each month's grid, i.e. inside the swipeable scroller — and a
     sticky element only sticks within its scroll container — so one shared
     row is drawn here instead, on the same .cal-grid template so its columns
     line up with the cells below; the per-month copies are hidden in CSS. */
  /* It lives INSIDE the sticky toolbar rather than pinning on its own: a
     sticky element is held by its parent box, and the calendar's box ends
     well before the toolbar's — so at the bottom of the page the two used to
     let go at different moments and slide over each other. One pinned unit
     can't come apart. */
  var calSticky = el("div", "cal-sticky");
  toolbar.appendChild(calSticky);
  calSticky.appendChild(calHead);
  var dowRow = el("div", "cal-grid cal-dow-row");
  dowRow.setAttribute("aria-hidden", "true");
  calSticky.appendChild(dowRow);
  var sheet = $("[data-rc-sheet]"), sheetSearch = $("[data-rc-sheet-search]"), sheetList = $("[data-rc-sheet-list]");
  var sheetApply = $("[data-rc-sheet-apply]"), fab = $("[data-rc-fab]");

  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }
  function chevron(dir, size) {
    var rot = { right: 0, down: 90, left: 180, up: 270 }[dir];
    var ns = "http://www.w3.org/2000/svg";
    var svg = document.createElementNS(ns, "svg");
    svg.setAttribute("class", "chevron"); svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("width", size || 24); svg.setAttribute("height", size || 24);
    svg.setAttribute("aria-hidden", "true"); svg.setAttribute("focusable", "false");
    svg.style.transform = "rotate(" + rot + "deg)";
    var p = document.createElementNS(ns, "path");
    p.setAttribute("d", "M9.5 5.5 16 12l-6.5 6.5"); p.setAttribute("fill", "none");
    p.setAttribute("stroke", "currentColor"); p.setAttribute("stroke-width", "2.4");
    p.setAttribute("stroke-linecap", "round"); p.setAttribute("stroke-linejoin", "round");
    svg.appendChild(p);
    return svg;
  }
  function empty(text) {
    var e = el("div", "empty");
    e.appendChild(el("div", "big", "눈_눈"));
    e.appendChild(document.createTextNode(text));
    return e;
  }
  function layoutChanged() { window.dispatchEvent(new Event("rc:layout")); }

  /* ————— useSlidingPill ————— */
  function slidePill(box, active) {
    var btn = box.querySelectorAll(":scope > button")[active];
    if (btn) {
      box.style.setProperty("--pill-x", btn.offsetLeft + "px");
      box.style.setProperty("--pill-w", btn.offsetWidth + "px");
      box.style.setProperty("--pill-o", "1");
    } else {
      box.style.setProperty("--pill-o", "0");
    }
  }
  function pills() {
    var calOn = view === "cal", listOn = view === "list" && !dayFocus;
    [].forEach.call(viewToggle.querySelectorAll(":scope > button"), function (b) {
      var on = b.getAttribute("data-rc-view") === "cal" ? calOn : listOn;
      b.classList.toggle("on", on);
      b.setAttribute("aria-selected", on ? "true" : "false");
    });
    slidePill(viewToggle, calOn ? 0 : listOn ? 1 : -1);
    [].forEach.call(fab.querySelectorAll(":scope > button"), function (b) {
      var on = b.getAttribute("data-rc-mode") === calMode;
      b.classList.toggle("on", on);
      b.setAttribute("aria-selected", on ? "true" : "false");
    });
    slidePill(fab, calMode === "month" ? 0 : 1);
    fab.classList.toggle("is-in", pastHero && view === "cal");
  }
  function measurePills() { pills(); }
  window.addEventListener("resize", measurePills);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(measurePills).catch(function () {});

  /* ————— Rail ————— */
  function renderRail() {
    var urgent = pool()
      .filter(function (r) { return r.releaseDate && daysUntil(r.releaseDate) >= 0 && daysUntil(r.releaseDate) <= 7; })
      .sort(function (a, b) { return a.releaseDate.localeCompare(b.releaseDate); })
      .slice(0, 14);
    railWrap.hidden = !urgent.length;
    railList.textContent = "";
    urgent.forEach(function (r) {
      var d = daysUntil(r.releaseDate);
      var a = el("a", "rail-card");
      a.href = r.url; a.target = "_blank"; a.rel = "noopener noreferrer";
      a.setAttribute("aria-label", fill(S.openProduct, { title: r.title }));
      if (r.image) { var img = el("img"); img.src = r.image; img.alt = ""; a.appendChild(img); }
      else a.appendChild(el("span", "noimg", "♪"));
      var b = el("span", "rail-card__body"), t = el("span", "rail-card__text");
      t.appendChild(el("span", "t", r.title));
      t.appendChild(el("span", "d", d === 0 ? S.today : fill(d === 1 ? S.daysLeftOne : S.daysLeftOther, { n: d })));
      b.appendChild(t);
      var cta = el("span", "rail-card__cta", S.viewProduct); cta.setAttribute("aria-hidden", "true");
      b.appendChild(cta);
      a.appendChild(b);
      railList.appendChild(a);
    });
  }

  /* ————— Toolbar ————— */
  function renderToolbar() {
    var n = artistsN;
    filtersBtn.classList.toggle("on", n > 0);
    artistToggle.classList.toggle("on", n > 0);
    [].forEach.call(page.querySelectorAll("[data-rc-count]"), function (c) { c.hidden = !n; c.textContent = n; });
    summary.hidden = !n;
    var f = filtered().length, p = pool().length;
    if (n) summaryText.textContent = fill(S.ofReleases, { shown: f, total: p });
    sheetApply.textContent = fill(S.showOf, { shown: f, total: p });
    layoutChanged();
  }

  /* ————— ArtistRow (picker + sheet) ————— */
  function artistRows(list, q) {
    list.textContent = "";
    var shown = artistCounts().filter(function (e) { return e[0].toLowerCase().indexOf(q.toLowerCase()) !== -1; });
    shown.forEach(function (e) {
      var a = e[0], sel = !!artists[a];
      var row = el("div", "artist-row" + (sel ? " sel" : ""));
      var pick = el("button", "artist-row__pick");
      pick.type = "button"; pick.setAttribute("role", "checkbox"); pick.setAttribute("aria-checked", sel ? "true" : "false");
      var box = el("span", "artist-row__box"); box.setAttribute("aria-hidden", "true");
      pick.appendChild(box);
      pick.appendChild(el("span", "artist-row__name", a));
      pick.appendChild(el("span", "n", String(e[1])));
      pick.addEventListener("click", function () { toggleArtist(a); });
      row.appendChild(pick);
      list.appendChild(row);
    });
    if (!shown.length) list.appendChild(el("p", "sheet__empty", S.noMatches));
  }
  function renderArtistLists() {
    if (pickerOpen) artistRows(artistList, pickerQ);
    if (filtersOpen) artistRows(sheetList, sheetQ);
  }
  function toggleArtist(a) {
    if (artists[a]) { delete artists[a]; artistsN--; } else { artists[a] = true; artistsN++; }
    render();
  }
  function clearFilters() { artists = {}; artistsN = 0; render(); }

  /* ————— CalendarGrid / CalendarHead ————— */
  function visibleCells(m, week) {
    var p = m.split("-").map(Number);
    var first = new Date(Date.UTC(p[0], p[1] - 1, 1));
    var dim = new Date(Date.UTC(p[0], p[1], 0)).getUTCDate();
    var lead = (first.getUTCDay() + 6) % 7;
    var cells = [], i;
    if (week) for (i = 0; i < 7; i++) cells.push(addDays(week, i));
    else {
      for (i = 0; i < lead; i++) cells.push(null);
      for (i = 1; i <= dim; i++) cells.push(m + "-" + String(i).padStart(2, "0"));
    }
    while (cells.length % 7) cells.push(null);
    return cells;
  }
  function groupByDay(list, cells) {
    var shown = {}, byDay = {};
    cells.forEach(function (c) { if (c) shown[c] = true; });
    list.forEach(function (r) {
      if (r.releaseDate && shown[r.releaseDate]) (byDay[r.releaseDate] = byDay[r.releaseDate] || []).push(r);
    });
    return byDay;
  }
  function countIn(byDay) { var n = 0; for (var k in byDay) n += byDay[k].length; return n; }
  var DOW = (function () {
    var out = [];
    for (var i = 0; i < 7; i++) out.push(fmtDate(addDays("2024-01-01", i), { weekday: "short" }).replace(/\.$/, ""));
    return out;
  })();
  function weekLabel(week) {
    var end = addDays(week, 6);
    if (week.slice(0, 7) === end.slice(0, 7)) {
      return +week.slice(8) + " – " + +end.slice(8) + " " + fmtDate(week, { month: "long" });
    }
    return fmtDate(week) + " – " + fmtDate(end);
  }

  function renderHead() {
    var week = calMode === "week" ? weekStart : null;
    var y = +month.slice(0, 4);
    var monthName = fmtDate(month + "-01", { month: "long" });
    var count = countIn(groupByDay(filtered(), visibleCells(month, week)));
    var prevDisabled = week ? addDays(week, -1) < bounds.min + "-01" : month <= bounds.min;
    var nextDisabled = week ? addDays(week, 7) > bounds.max + "-31" : month >= bounds.max;

    calHead.textContent = "";
    var prev = el("button", "cal-nav"); prev.type = "button"; prev.disabled = prevDisabled;
    prev.setAttribute("aria-label", week ? S.prevWeek : S.prevMonth);
    prev.appendChild(chevron("left"));
    prev.addEventListener("click", function () { week ? shiftWeek(-1) : shiftMonth(-1); });
    var h2 = el("h2");
    h2.appendChild(document.createTextNode((week ? weekLabel(week) : monthName) + " "));
    h2.appendChild(el("span", null, String(week ? +addDays(week, 6).slice(0, 4) : y)));
    var next = el("button", "cal-nav"); next.type = "button"; next.disabled = nextDisabled;
    next.setAttribute("aria-label", week ? S.nextWeek : S.nextMonth);
    next.appendChild(chevron("right"));
    next.addEventListener("click", function () { week ? shiftWeek(1) : shiftMonth(1); });
    calHead.appendChild(prev); calHead.appendChild(h2); calHead.appendChild(next);
    calHead.appendChild(el("span", "filter-summary" + (countDark ? " is-on-dark" : ""), releasesLabel(count)));
  }

  function calendarGrid(m) {
    var today = todayISO();
    var cells = visibleCells(m, null);
    var byDay = groupByDay(filtered(), cells);
    var frag = document.createDocumentFragment();
    var grid = el("div", "cal-grid");
    DOW.forEach(function (d) { grid.appendChild(el("div", "cal-dow", d)); });
    cells.forEach(function (day) {
      if (!day) { grid.appendChild(el("div", "cal-cell off")); return; }
      var rs = byDay[day] || [];
      var cell = el("div", ["cal-cell", rs.length ? "has" : "", rs.length > 1 ? "many" : "", day === today ? "today-cell" : ""].join(" "));
      cell.appendChild(el("span", "cal-daynum", String(+day.slice(8))));
      if (rs.length) {
        cell.setAttribute("role", "button"); cell.tabIndex = 0;
        cell.title = rs.length === 1 ? fill(S.cellOne, { title: rs[0].title }) : fill(S.cellMany, { n: rs.length });
        cell.addEventListener("click", function () { openDay(day); });
        cell.addEventListener("keydown", function (e) { if (e.key === "Enter") openDay(day); });

        var round = el("span", "cal-round" + (rs.length > 1 ? " has-count" : ""));
        if (rs[0].image) { var ri = el("img"); ri.src = rs[0].image; ri.alt = ""; ri.loading = "lazy"; round.appendChild(ri); }
        if (rs.length > 1) round.appendChild(el("span", "cal-round__n", String(rs.length)));
        cell.appendChild(round);

        var stamps = el("div", "cal-stamps");
        rs.slice(0, 2).forEach(function (r, si) {
          var st = el("span", "cal-stamp cal-stamp--" + si);
          var edge = el("span", "cal-stamp__edge"); edge.setAttribute("aria-hidden", "true");
          var art = el("span", "cal-stamp__art");
          if (r.image) { var im = el("img"); im.src = r.image; im.alt = r.title; im.loading = "lazy"; art.appendChild(im); }
          else art.appendChild(el("span", "cal-stamp__ph", "♪"));
          st.appendChild(edge); st.appendChild(art);
          stamps.appendChild(st);
        });
        if (rs.length > 1) stamps.appendChild(el("span", "cal-stamps__n", String(rs.length)));
        cell.appendChild(stamps);
        cell.appendChild(el("span", "cal-badge", String(rs.length)));
      }
      grid.appendChild(cell);
    });
    frag.appendChild(grid);
    if (!countIn(byDay)) frag.appendChild(empty(S.noReleasesMonth));
    return frag;
  }

  /* ————— Month carousel ————— */
  var monthsEl = null, offsets = [];
  /* Each month's scrollLeft. Measured against the scroller itself: offsetLeft
     is relative to the nearest positioned ancestor, which includes the page
     gutter — fine for picking the nearest month, 10–20px out for landing on one. */
  function readOffsets() {
    if (!monthsEl) { offsets = []; return; }
    var base = monthsEl.getBoundingClientRect().left - monthsEl.scrollLeft;
    offsets = [].map.call(monthsEl.children, function (c) { return Math.round(c.getBoundingClientRect().left - base); });
  }
  /* The scroller would otherwise be as tall as the tallest of the twelve months
     (a six-week month, or one with the "no releases" note), leaving a dead band
     under a five-week month. Sized to the month on screen instead, plus the
     scroller's own glow padding. */
  function fitMonths() {
    if (!monthsEl) return;
    var slide = monthsEl.children[months.indexOf(month)];
    if (!slide) return;
    var cs = getComputedStyle(monthsEl);
    var h = slide.offsetHeight + (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    monthsEl.style.height = h + "px";
    layoutChanged();
  }
  function renderMonths() {
    monthsEl = el("div", "cal-months");
    monthsEl.setAttribute("role", "group");
    months.forEach(function (m) {
      var slide = el("div", "cal-month");
      if (m !== month) slide.inert = true;
      slide.appendChild(calendarGrid(m));
      monthsEl.appendChild(slide);
    });
    monthsEl.addEventListener("scroll", onMonthScroll, { passive: true });
    monthsEl.addEventListener("scrollend", settleMonths);
    monthsEl.addEventListener("touchstart", function () { touching = true; clearTimeout(settleTimer); }, { passive: true });
    monthsEl.addEventListener("touchend", function () { touching = false; scheduleSettle(); }, { passive: true });
    monthsEl.addEventListener("touchcancel", function () { touching = false; scheduleSettle(); }, { passive: true });
    body.appendChild(monthsEl);
    readOffsets();
    var target = monthsEl.children[months.indexOf(month)];
    if (target) monthsEl.scrollLeft = offsets[months.indexOf(month)];
    fitMonths();
    if (window.ResizeObserver) {
      var scroller = monthsEl;
      new ResizeObserver(function () { if (scroller === monthsEl) { readOffsets(); fitMonths(); } }).observe(target || scroller);
    }
  }
  function nearestMonth() {
    var best = 0, x = monthsEl.scrollLeft;
    for (var i = 1; i < offsets.length; i++) if (Math.abs(offsets[i] - x) < Math.abs(offsets[best] - x)) best = i;
    return best;
  }
  /* During a swipe only the heading follows along. Resizing the scroller or
     making the slide under the finger inert mid-gesture is what made iOS
     Safari abandon its snap and leave the grid parked between two months — so
     both wait until the swipe has fully settled (finger up, momentum done). */
  var touching = false, settleTimer = 0;
  function scheduleSettle() {
    clearTimeout(settleTimer);
    if (!touching) settleTimer = setTimeout(settleMonths, 140);
  }
  function settleMonths() {
    if (!monthsEl || !offsets.length || touching) return;
    var best = nearestMonth();
    var target = Math.min(offsets[best], monthsEl.scrollWidth - monthsEl.clientWidth);
    // Not on a month boundary (snap was interrupted): finish the snap ourselves.
    if (Math.abs(monthsEl.scrollLeft - target) > 1) {
      monthsEl.scrollTo({ left: target, behavior: "smooth" });
      return; // its own scroll events bring us back here once it lands
    }
    [].forEach.call(monthsEl.children, function (c, i) { c.inert = i !== best; });
    fitMonths();
  }
  function onMonthScroll() {
    if (!offsets.length) return;
    var best = nearestMonth();
    if (months[best] !== month) { month = months[best]; renderHead(); }
    scheduleSettle();
  }
  function goToMonth(m) {
    var i = months.indexOf(m);
    var target = i >= 0 && monthsEl ? monthsEl.children[i] : null;
    if (target) monthsEl.scrollTo({ left: offsets[i], behavior: "smooth" });
    else { month = m; renderHead(); }
  }
  function shiftMonth(dir) {
    var i = months.indexOf(month);
    var next = months[Math.min(months.length - 1, Math.max(0, i + dir))];
    if (next) goToMonth(next);
  }

  /* ————— Week view (WeekAgenda) ————— */
  function weekDefault() {
    var today = todayISO(), days = [], byDay = weekByDay();
    for (var i = 0; i < 7; i++) days.push(addDays(weekStart, i));
    if (days.indexOf(today) !== -1) return today;
    for (i = 0; i < 7; i++) if (byDay[days[i]]) return days[i];
    return days[0];
  }
  function weekByDay() {
    var first = weekStart, last = addDays(weekStart, 6), byDay = {};
    filtered().forEach(function (r) {
      if (r.releaseDate && r.releaseDate >= first && r.releaseDate <= last) (byDay[r.releaseDate] = byDay[r.releaseDate] || []).push(r);
    });
    return byDay;
  }
  function renderWeek() {
    var today = todayISO(), byDay = weekByDay();
    if (!weekSel) weekSel = weekDefault();
    var wks = el("div", "wks");
    var strip = el("div", "wks-strip");
    strip.setAttribute("role", "tablist");
    for (var i = 0; i < 7; i++) (function (d) {
      var has = byDay[d], sel = d === weekSel;
      var b = el("button", "wks-day" + (sel ? " on" : "") + (d === today ? " today" : ""));
      b.type = "button"; b.setAttribute("role", "tab"); b.setAttribute("aria-selected", sel ? "true" : "false");
      b.setAttribute("aria-label", fmtDate(d, { weekday: "long" }) + " " + +d.slice(8) + ", " + releasesLabel(has ? has.length : 0));
      var dow = el("span", "wks-day__dow", fmtDate(d, { weekday: "short" }).replace(/\.$/, ""));
      var num = el("span", "wks-day__num", String(+d.slice(8)));
      var dot = el("span", "wks-day__dot" + (has ? " has" : ""));
      [dow, num, dot].forEach(function (x) { x.setAttribute("aria-hidden", "true"); b.appendChild(x); });
      b.addEventListener("click", function () { weekSel = d; render(); });
      strip.appendChild(b);
    })(addDays(weekStart, i));
    wks.appendChild(strip);
    var list = el("div", "wks-list");
    var rows = byDay[weekSel] || [];
    rows.forEach(function (r, idx) { list.appendChild(feedRow(r, idx)); });
    if (!rows.length) {
      list.appendChild(empty(fill(S.noReleasesOn, {
        day: fmtDate(weekSel, { weekday: "long" }) + (weekSel === today ? " (" + S.todayWord + ")" : ""),
      })));
    }
    wks.appendChild(list);
    body.appendChild(wks);
  }
  function setMode(m) {
    if (m === "week" && calMode !== "week") {
      var t = todayISO();
      weekStart = mondayOf(t.slice(0, 7) === month ? t : month + "-01");
      weekSel = null;
    }
    calMode = m;
    render();
  }
  function shiftWeek(dir) {
    weekStart = addDays(weekStart, dir * 7);
    month = addDays(weekStart, 3).slice(0, 7);
    weekSel = null;
    render();
  }

  /* ————— FeedRow ————— */
  function feedRow(r, index) {
    var price = priceDisplay(r);
    var row = el("article", "frow");
    row.setAttribute("data-release", r.id);
    row.style.animationDelay = Math.min(index, 6) * 40 + "ms";
    var hit = el("a", "frow__hit");
    hit.setAttribute("aria-hidden", "true"); hit.tabIndex = -1;
    hit.href = r.url; hit.target = "_blank"; hit.rel = "noopener noreferrer";
    row.appendChild(hit);
    if (r.image) { var img = el("img"); img.src = r.image; img.alt = r.title; img.loading = "lazy"; row.appendChild(img); }
    else row.appendChild(el("div", "noimg", "♪"));

    var mainCol = el("div", "frow-main");
    var head = el("div", "frow-head");
    head.appendChild(el("span", "artist", r.artist));
    head.appendChild(el("span", "title", r.title));
    mainCol.appendChild(head);
    var meta = el("div", "frow-meta");
    if (r.variants.length > 1) meta.appendChild(el("span", null, fill(S.versionsCountOther, { n: r.variants.length })));
    var ship = el("span", "ship");
    var truck = el("span", "ico ico--truck"); truck.setAttribute("aria-hidden", "true");
    ship.appendChild(truck);
    ship.appendChild(document.createTextNode(r.shipLabel || S.shipTba));
    meta.appendChild(ship);
    mainCol.appendChild(meta);
    row.appendChild(mainCol);

    var side = el("div", "frow-side");
    var p = el("span", "price");
    p.appendChild(el("span", price.was ? "now on-sale" : "now", price.now));
    if (price.was) p.appendChild(el("s", "was", price.was));
    side.appendChild(p);
    // Notify is off and Follow hidden, as in the prototype — nothing to act on.
    side.appendChild(el("div", "mini-actions is-empty"));
    row.appendChild(side);
    return row;
  }

  /* ————— Feed ————— */
  function dayHeader(day, today) {
    var wrap = el("div", "feed-date" + (day === today ? " today-hdr" : ""));
    var pill = el("span", "feed-date__pill");
    pill.appendChild(el("b", null, String(+day.slice(8))));
    pill.appendChild(document.createTextNode(" " + fmtDate(day, { month: "long" }) + " · " + (day === today ? S.todayWord : fmtDate(day, { weekday: "long" }))));
    wrap.appendChild(pill);
    return wrap;
  }
  var backBarRO = null;
  function publishBackbar(bar) {
    if (backBarRO) { backBarRO.disconnect(); backBarRO = null; }
    if (!bar) { docEl.style.setProperty("--backbar-h", "0px"); return; }
    var pub = function () { docEl.style.setProperty("--backbar-h", bar.offsetHeight + "px"); layoutChanged(); };
    pub();
    if (window.ResizeObserver) { backBarRO = new ResizeObserver(pub); backBarRO.observe(bar); }
  }
  function renderFeed() {
    var today = todayISO(), list = filtered();
    var feed = el("div", "feed");
    if (dayFocus) {
      var bar = el("div", "day-focus-bar");
      var back = el("button", "back-btn");
      back.type = "button";
      back.appendChild(chevron("left", 18));
      back.appendChild(document.createTextNode(S.backToCalendar));
      back.addEventListener("click", backToCalendar);
      bar.appendChild(back);
      feed.appendChild(bar);
      feed.appendChild(dayHeader(dayFocus, today));
      var rows = list.filter(function (r) { return r.releaseDate === dayFocus; });
      rows.forEach(function (r, i) { feed.appendChild(feedRow(r, i)); });
      if (!rows.length) feed.appendChild(empty(S.noReleasesDay));
      body.appendChild(feed);
      publishBackbar(bar);
      return;
    }
    publishBackbar(null);
    if (!list.length) { body.appendChild(empty(S.noReleasesFiltered)); return; }
    var byDay = {};
    var past = [];
    list.forEach(function (r) {
      if (r.releaseDate && r.releaseDate >= today) (byDay[r.releaseDate] = byDay[r.releaseDate] || []).push(r);
      else past.push(r);
    });
    Object.keys(byDay).sort().forEach(function (d) {
      var sec = el("section", "feed-day");
      sec.appendChild(dayHeader(d, today));
      byDay[d].forEach(function (r, i) { sec.appendChild(feedRow(r, i)); });
      feed.appendChild(sec);
    });
    if (past.length) {
      var ps = el("section", "feed-day");
      var ph = el("div", "feed-date");
      ph.appendChild(el("span", "feed-date__pill", S.alreadyReleased));
      ps.appendChild(ph);
      past.forEach(function (r, i) { ps.appendChild(feedRow(r, i)); });
      feed.appendChild(ps);
    }
    body.appendChild(feed);
  }

  /* ————— Render ————— */
  function render() {
    renderToolbar();
    body.textContent = "";
    monthsEl = null;
    calSticky.hidden = view !== "cal";
    toolbar.classList.toggle("has-cal", view === "cal");
    // Week view has its own day strip; the shared weekday row is month-only.
    dowRow.hidden = calMode === "week";
    if (!dowRow.childNodes.length) DOW.forEach(function (d) { dowRow.appendChild(el("div", "cal-dow", d)); });
    main.classList.toggle("is-list", view !== "cal");
    if (view === "cal") {
      publishBackbar(null);
      renderHead();
      if (calMode === "week") renderWeek(); else renderMonths();
    } else {
      renderFeed();
    }
    renderArtistLists();
    pills();
    layoutChanged();
  }

  /* ————— Views, day focus, the platform back gesture ————— */
  var dayEntry = false;
  try {
    var hs = history.state;
    if (hs && hs.nolaeDayFocus) { var clean = {}; for (var k in hs) if (k !== "nolaeDayFocus") clean[k] = hs[k]; history.replaceState(clean, ""); }
  } catch (e) {}
  function dropDayEntry() {
    if (!dayEntry) return;
    dayEntry = false;
    history.back();
  }
  function switchView(v) { dropDayEntry(); dayFocus = null; view = v; render(); }
  function backToCalendar() {
    dropDayEntry();
    if (dayFocus) month = dayFocus.slice(0, 7);
    dayFocus = null; view = "cal"; render();
  }
  window.addEventListener("popstate", function () {
    if (!dayEntry) return;
    dayEntry = false;
    if (dayFocus) month = dayFocus.slice(0, 7);
    dayFocus = null; view = "cal"; render();
  });
  function scrollToY(y) {
    if (window.__lenis && typeof window.__lenis.scrollTo === "function") window.__lenis.scrollTo(y, { duration: 0.8 });
    else window.scrollTo({ top: y, behavior: "smooth" });
  }
  function openDay(day) {
    dayFocus = day; view = "list";
    if (!dayEntry) { dayEntry = true; try { history.pushState(Object.assign({}, history.state, { nolaeDayFocus: true }), ""); } catch (e) {} }
    render();
    /* Park the day list just under the pinned chrome, once it has rendered
       (swapping a month grid for a short list collapses the page height). */
    var pinned = getComputedStyle(toolbar).position === "sticky";
    var headerH = parseFloat(getComputedStyle(docEl).getPropertyValue("--chrome-top")) || 0;
    var back = body.querySelector(".day-focus-bar");
    var chrome = headerH + (pinned ? toolbar.offsetHeight : 0) + (back ? back.offsetHeight : 0);
    var delta = main.getBoundingClientRect().top - chrome;
    if (Math.abs(delta) >= 2) scrollToY(window.scrollY + delta);
  }

  /* ————— Controls ————— */
  [].forEach.call(viewToggle.querySelectorAll(":scope > button"), function (b) {
    b.addEventListener("click", function () { switchView(b.getAttribute("data-rc-view")); });
  });
  [].forEach.call(fab.querySelectorAll(":scope > button"), function (b) {
    b.addEventListener("click", function () { setMode(b.getAttribute("data-rc-mode")); });
  });

  function setPicker(open) {
    pickerOpen = open;
    artistPop.hidden = !open;
    artistToggle.setAttribute("aria-expanded", open ? "true" : "false");
    if (open) { pickerQ = ""; artistSearch.value = ""; renderArtistLists(); artistSearch.focus({ preventScroll: true }); }
  }
  artistToggle.addEventListener("click", function () { setPicker(!pickerOpen); });
  document.addEventListener("mousedown", function (e) { if (pickerOpen && !artistPicker.contains(e.target)) setPicker(false); });
  artistSearch.addEventListener("input", function () { pickerQ = artistSearch.value; artistRows(artistList, pickerQ); });

  function setSheet(open) {
    filtersOpen = open;
    sheet.hidden = !open;
    docEl.classList.toggle("rc-sheet-open", open);
    if (open) { sheetQ = ""; sheetSearch.value = ""; renderArtistLists(); }
  }
  filtersBtn.addEventListener("click", function () { setSheet(true); });
  sheet.addEventListener("click", function (e) { if (e.target === sheet) setSheet(false); });
  [].forEach.call(sheet.querySelectorAll("[data-rc-sheet-close]"), function (b) { b.addEventListener("click", function () { setSheet(false); }); });
  sheetSearch.addEventListener("input", function () { sheetQ = sheetSearch.value; artistRows(sheetList, sheetQ); });
  [].forEach.call(page.querySelectorAll("[data-rc-clear]"), function (b) { b.addEventListener("click", clearFilters); });
  document.addEventListener("keydown", function (e) { if (e.key === "Escape") { setPicker(false); setSheet(false); } });

  /* --toolbar-h: the sticky day sleeves park under the toolbar's live height. */
  function publishToolbar() { docEl.style.setProperty("--toolbar-h", toolbar.offsetHeight + "px"); }
  publishToolbar();
  if (window.ResizeObserver) new ResizeObserver(publishToolbar).observe(toolbar);

  /* The floating month/week switch shows once 80% of the hero is behind you. */
  var hero = page.querySelector(".hero");
  function checkHero() {
    if (!hero) return;
    var r = hero.getBoundingClientRect();
    var v = r.bottom <= r.height * (1 - HERO_CROSSED);
    if (v !== pastHero) { pastHero = v; fab.classList.toggle("is-in", pastHero && view === "cal"); }
  }
  window.addEventListener("scroll", checkHero, { passive: true });
  window.addEventListener("resize", checkHero);
  checkHero();

  /* ————— Release count over the glass —————
     The count beside the month name is light grey, which disappears when a
     dark cover scrolls under the floating heading. Like the theme's header
     colour-adapt, it samples what is actually behind it — skipping the
     floating chrome and the glass, taking a cover image's average brightness
     or the first solid background — and goes white over dark, back to its own
     grey over light. Samples three points across the text, at most once a
     frame. */
  var LUMA = 0.6;
  function luminance(rgb) {
    var lin = rgb.slice(0, 3).map(function (v) {
      var c = v / 255;
      return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
  }
  function solidBg(node) {
    var m = /^rgba?\(([^)]+)\)/.exec(getComputedStyle(node).backgroundColor || "");
    if (!m) return null;
    var p = m[1].split(",").map(parseFloat);
    return p.length > 3 && p[3] < 0.5 ? null : p;
  }
  var imgLumaCache = {};
  function imageLuma(img) {
    var src = img.currentSrc || img.src;
    if (!src) return null;
    var hit = imgLumaCache[src];
    if (hit !== undefined) return hit === "pending" ? null : hit;
    imgLumaCache[src] = "pending";
    var probe = new Image();
    probe.crossOrigin = "anonymous";
    probe.onload = function () {
      try {
        var c = document.createElement("canvas");
        c.width = 8; c.height = 8;
        var ctx = c.getContext("2d");
        ctx.drawImage(probe, 0, 0, 8, 8);
        var d = ctx.getImageData(0, 0, 8, 8).data, sum = 0;
        for (var i = 0; i < d.length; i += 4) sum += luminance([d[i], d[i + 1], d[i + 2]]);
        imgLumaCache[src] = sum / (d.length / 4);
      } catch (e) { imgLumaCache[src] = 1; }
      queueCount();
    };
    probe.onerror = function () { imgLumaCache[src] = 1; };
    probe.src = src;
    return null;
  }
  function lumaAt(x, y) {
    // Topmost first. Only what lies UNDER the toolbar counts — anything stacked
    // above it (a newsletter popup's dim overlay, a drawer) is not a backdrop.
    var stack = document.elementsFromPoint(x, y), under = false;
    for (var i = 0; i < stack.length; i++) {
      var node = stack[i];
      if (toolbar.contains(node)) { under = true; continue; }
      if (!under || node.classList.contains("rc-glass")) continue;
      if (node.tagName === "IMG") return imageLuma(node);
      /* The phone's round cover: its white fill is only a backstop, and a busy
         day lays a 50% black scrim (::after) over the art — which is why the
         circle itself, not the image, is reported topmost. Read the image,
         darkened by the scrim (half the sRGB ≈ 0.22 of the luminance). */
      if (node.classList.contains("cal-round")) {
        var art = node.querySelector("img");
        if (art) {
          var l = imageLuma(art);
          return l === null ? null : node.classList.contains("has-count") ? l * 0.22 : l;
        }
      }
      var bg = solidBg(node);
      if (bg) return luminance(bg);
    }
    return 1;
  }
  /* Each piece of floating text decides for itself: the count samples three
     points across its width, each weekday letter just its own centre — so only
     the letters actually over a dark cover go white. The count is redrawn on
     every month change, so its state is remembered and re-applied (renderHead)
     rather than flickering back for a frame. The month name keeps its colours. */
  var countQueued = false, countDark = false;
  /** true = dark behind it, false = light, null = not decidable yet. */
  function darkBehind(node, points) {
    var r = node.getBoundingClientRect();
    if (!r.width || r.bottom < 0 || r.top > window.innerHeight) return null;
    var y = Math.round(r.top + r.height / 2), sum = 0, n = 0;
    points.forEach(function (t) {
      var l = lumaAt(Math.round(r.left + r.width * t), y);
      if (l !== null) { sum += l; n++; }
    });
    return n ? sum / n < LUMA : null; // null: covers still loading — imageLuma() re-queues us
  }
  function adaptCount() {
    countQueued = false;
    if (calSticky.hidden) return;
    var count = calHead.querySelector(".filter-summary");
    var d = count && darkBehind(count, [0.15, 0.5, 0.85]);
    if (d !== null && count) { countDark = d; count.classList.toggle("is-on-dark", d); }
    if (!dowRow.hidden) [].forEach.call(dowRow.children, function (label) {
      var dl = darkBehind(label, [0.5]);
      if (dl !== null) label.classList.toggle("is-on-dark", dl);
    });
  }
  function queueCount() {
    if (countQueued) return;
    countQueued = true;
    requestAnimationFrame(adaptCount);
  }
  window.addEventListener("scroll", queueCount, { passive: true });
  window.addEventListener("resize", queueCount);
  window.addEventListener("rc:layout", queueCount);
  // A month swipe changes what is behind it too.
  document.addEventListener("scroll", function (e) { if (e.target && e.target.classList && e.target.classList.contains("cal-months")) queueCount(); }, { capture: true, passive: true });

  if (S.todayCell) docEl.style.setProperty("--rc-today-label", JSON.stringify(S.todayCell));

  /* ————— Boot: draw from page one straight away, then again with everything ————— */
  var seed = readPage(document);
  releases = toReleases(seed.items);
  renderRail();
  render();
  requestAnimationFrame(function () {
    viewToggle.classList.add("is-ready");
    fab.classList.add("is-ready");
  });

  var rest = [];
  for (var p = 2; p <= seed.pages; p++) rest.push(p);
  var items = seed.items.slice();
  (function next(i) {
    if (i >= rest.length) {
      releases = toReleases(items);
      renderRail();
      render();
      return;
    }
    Promise.all(rest.slice(i, i + 4).map(fetchPage)).then(function (pages) {
      pages.forEach(function (list) { items = items.concat(list); });
      next(i + 4);
    }).catch(function (err) {
      if (window.console) console.error("[release-calendar]", err);
      releases = toReleases(items);
      renderRail();
      render();
    });
  })(0);
})();
